// Traditional→Simplified Chinese conversion (OpenCC t2s data). The models
// are trained on simplified text — traditional input is out-of-distribution
// and drifts entities/meaning hard (measured 2026-07-07: chapter-8 phồn thể
// vs the same text giản thể). The CT2/qt2 reference pipeline converts before
// translating; this mirrors it client-side.
//
// Longest-match first over the phrase exceptions (protective identities like
// 乾坤→乾坤 and multi-char overrides like 一目瞭然→一目了然), then the
// per-char map. Simplified input passes through unchanged (identity), so the
// conversion is always applied — no detection heuristics to get wrong.
import { T2S_CHARS, T2S_PHRASES } from './t2s_data.js';

let charMap = null;
let phraseIndex = null; // first char -> phrases sorted longest-first

function init() {
  charMap = new Map(Object.entries(T2S_CHARS));
  phraseIndex = new Map();
  for (const key of Object.keys(T2S_PHRASES)) {
    const first = key[0];
    let list = phraseIndex.get(first);
    if (!list) phraseIndex.set(first, (list = []));
    list.push(key);
  }
  for (const list of phraseIndex.values()) list.sort((a, b) => b.length - a.length);
}

// Returns { text, changed } — changed counts replaced units (phrases count 1).
export function t2s(text) {
  if (!charMap) init();
  let out = '';
  let changed = 0;
  let i = 0;
  const n = text.length;
  while (i < n) {
    // Code-point aware: some map keys are astral-plane hanzi (2 UTF-16 units).
    const c = String.fromCodePoint(text.codePointAt(i));
    const candidates = phraseIndex.get(c);
    if (candidates) {
      let matched = null;
      for (const p of candidates) {
        if (text.startsWith(p, i)) { matched = p; break; }
      }
      if (matched) {
        out += T2S_PHRASES[matched];
        i += matched.length;
        changed++;
        continue;
      }
    }
    const mapped = charMap.get(c);
    if (mapped !== undefined) {
      out += mapped;
      changed++;
    } else {
      out += c;
    }
    i += c.length;
  }
  // Nothing replaced → hand back the input string itself: `out` (a fresh
  // char-by-char rope the size of the whole novel) becomes garbage right
  // here instead of doubling the document for the rest of the run, and the
  // sentence slices downstream all anchor into the one original string.
  return { text: changed ? out : text, changed };
}

// Buffered equivalent for app-layer scans over whole novels. The translation
// engine keeps the long-established t2s() path above untouched; this variant
// avoids retaining one rope node per source character while an auxiliary
// consumer (such as document-frequency counting) holds the canonical text.
export function t2sBuffered(text, flushEvery = 4096) {
  if (!charMap) init();
  const chunks = [];
  const buffer = [];
  let changed = 0;
  let i = 0;
  const push = (value) => {
    buffer.push(value);
    if (buffer.length >= flushEvery) {
      chunks.push(buffer.join(''));
      buffer.length = 0;
    }
  };
  while (i < text.length) {
    const c = String.fromCodePoint(text.codePointAt(i));
    const candidates = phraseIndex.get(c);
    if (candidates) {
      let matched = null;
      for (const phrase of candidates) {
        if (text.startsWith(phrase, i)) { matched = phrase; break; }
      }
      if (matched) {
        push(T2S_PHRASES[matched]);
        i += matched.length;
        changed++;
        continue;
      }
    }
    const mapped = charMap.get(c);
    if (mapped !== undefined) {
      push(mapped);
      changed++;
    } else {
      push(c);
    }
    i += c.length;
  }
  if (buffer.length) chunks.push(buffer.join(''));
  // Same identity fast path as t2s(): an already-simplified document keeps
  // its original string instead of materializing an equal copy.
  return { text: changed ? chunks.join('') : text, changed };
}
